import React, { useState, useMemo, useEffect } from "react";
import { FaEye, FaArrowLeft, FaCheck, FaTimes } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net";

export default function MyAssetsTable({ assets, onReturn, onView, loggedInStaffId, refreshAssets }) {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [actionLoading, setActionLoading] = useState(null); // Tracks which asset ID is currently being acted upon
    const itemsPerPage = 10;

    // Filter + Sort assets by newest first (createdAt DESC)
    const filteredAssets = useMemo(() => {
        const q = search.trim().toLowerCase();
        let data = [...assets];

        data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (!q) return data;

        return data.filter((a) => {
            const assignedToName = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : '';
            const custodianName = a.custodian ? `${a.custodian.firstName} ${a.custodian.lastName}` : '';
            const projectName = a.projectId ? a.projectId.projectTitle : '';
            // Search by project name and role
            const searchableString = `${a.name || ''} ${a.category || ''} ${a.status || ''} ${a.location || ''} ${a.serialNumber || ''} ${assignedToName} ${custodianName} ${projectName}`.toLowerCase();

            return searchableString.includes(q);
        });
    }, [assets, search]);

    // Reset to page 1 if search or data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search, assets]);

    // Paginated data
    const paginatedAssets = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredAssets.slice(start, start + itemsPerPage);
    }, [filteredAssets, currentPage]);

    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

    const getStatusColor = (status) => {
        switch (status) {
            case "Active": return 'bg-green-100 text-green-800';
            case "In Use": return 'bg-blue-100 text-blue-800';
            case "Needs Review": return 'bg-yellow-100 text-yellow-800';
            case "Pending Acceptance": return 'bg-orange-100 text-orange-800'; // Added
            case "Pending Return": return 'bg-purple-100 text-purple-800';
            case "Returned": return 'bg-gray-200 text-gray-700';
            case "Rejected": return 'bg-red-100 text-red-800'; // Added
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // --- NEW: Function to determine the logged-in staff's role for the asset ---
    const getMyRole = (asset) => {
        if (!loggedInStaffId) return "N/A";
        const isAssigned = asset.assignedTo && asset.assignedTo._id === loggedInStaffId;
        const isCustodian = asset.custodian && asset.custodian._id === loggedInStaffId;

        if (isAssigned && isCustodian) return "Assigned & Custodian";
        if (isAssigned) return "Assigned To";
        if (isCustodian) return "Custodian";
        return "N/A";
    };

    // --- Handlers for Accept/Reject ---
    const handleAccept = async (assetId) => {
        setActionLoading(assetId);
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_URL}/api/assets/${assetId}/confirm`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Asset accepted successfully! 🎉");
            if (refreshAssets) refreshAssets();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to accept asset");
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (assetId) => {
        // Using prompt for simplicity. In a larger app, use a Modal.
        const reason = window.prompt("Please enter a reason for rejecting this assignment:");
        if (!reason) return;

        setActionLoading(assetId);
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_URL}/api/assets/${assetId}/reject`, { notes: reason }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Asset rejected and returned to inventory.");
            if (refreshAssets) refreshAssets();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to reject asset");
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <div className="mt-8 bg-white rounded shadow p-4">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">My Assets</h2>

            {/* Search */}
            <div className="flex justify-between items-center mb-4 gap-3">
                <input
                    type="search"
                    placeholder="Search your assets by name, category, project..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="bg-orange-100 text-gray-600 text-left">
                            <th className="px-4 py-2 text-orange-600">Name</th>
                            <th className="px-4 py-2 text-orange-600">Category</th>
                            <th className="px-4 py-2 text-orange-600">Serial Number</th>
                            <th className="px-4 py-2 text-orange-600">Status</th>
                            <th className="px-4 py-2 text-orange-600">Location</th>
                            <th className="px-4 py-2 text-orange-600">Project</th>
                            <th className="px-4 py-2 text-orange-600">My Role</th>
                            <th className="px-4 py-2 text-orange-600 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedAssets.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="text-center text-gray-500 py-4">
                                    No assets found assigned to or managed by you.
                                </td>
                            </tr>
                        ) : (
                            paginatedAssets.map((asset) => (
                                <tr key={asset._id} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium text-gray-900">{asset.name}</td>
                                    <td className="px-4 py-2 text-gray-500">{asset.category}</td>
                                    <td className="px-4 py-2 text-gray-500">{asset.serialNumber}</td>
                                    <td className="px-4 py-2">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(asset.status)}`}>
                                            {asset.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-500">{asset.location || "N/A"}</td>
                                    <td className="px-4 py-2 text-gray-500">
                                        {asset.projectId ? asset.projectId.projectTitle : "N/A"}
                                    </td>
                                    <td className="px-4 py-2 text-gray-500">
                                        {getMyRole(asset)}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                        <div className="flex justify-end items-center gap-2">
                                            {/* View Button - Always Visible */}
                                            <button 
                                                onClick={() => onView(asset)} 
                                                className="text-orange-600 hover:text-orange-900 p-1" 
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>

                                            {/* --- Conditional Action Buttons --- */}
                                            
                                            {/* 1. Pending Acceptance: Show Accept & Reject */}
                                            {asset.status === "Pending Acceptance" && (
                                                <>
                                                    <button 
                                                        onClick={() => handleAccept(asset._id)} 
                                                        disabled={actionLoading === asset._id}
                                                        className="text-green-600 hover:text-green-900 p-1 bg-green-50 rounded-full transition-colors" 
                                                        title="Accept Assignment"
                                                    >
                                                        {actionLoading === asset._id ? '...' : <FaCheck />}
                                                    </button>
                                                    <button 
                                                        onClick={() => handleReject(asset._id)} 
                                                        disabled={actionLoading === asset._id}
                                                        className="text-red-600 hover:text-red-900 p-1 bg-red-50 rounded-full transition-colors" 
                                                        title="Reject Assignment"
                                                    >
                                                        {actionLoading === asset._id ? '...' : <FaTimes />}
                                                    </button>
                                                </>
                                            )}

                                            {/* 2. Active: Show Return Button */}
                                            {asset.status === "Active" && (
                                                <button 
                                                    onClick={() => onReturn(asset)} 
                                                    className="text-orange-600 hover:text-orange-900 p-1" 
                                                    title="Return Asset"
                                                >
                                                    <FaArrowLeft />
                                                </button>
                                            )}

                                            {/* 3. Pending Return: Show Status Text */}
                                            {asset.status === "Pending Return" && (
                                                <span className="text-xs text-purple-600 font-medium bg-purple-50 px-2 py-1 rounded">
                                                    Return Pending
                                                </span>
                                            )}

                                            {/* 4. Returned: Show Status Text */}
                                            {asset.status === "Returned" && (
                                                <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                                                    Returned
                                                </span>
                                            )}
                                            
                                            {/* 5. Rejected: Show Status Text */}
                                            {asset.status === "Rejected" && (
                                                <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                                                    Rejected
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`rounded px-3 py-1 ${currentPage === 1 ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`rounded px-3 py-1 ${currentPage === i + 1 ? "bg-orange-300 text-orange-900 font-semibold" : "bg-gray-200 hover:bg-gray-300"}`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`rounded px-3 py-1 ${currentPage === totalPages ? "cursor-not-allowed bg-gray-300 text-gray-500" : "bg-orange-100 text-orange-600 hover:bg-orange-200"}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}